import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../services/auth';

// Сеть/5xx на старте (перезапуск auth-service или шлюза) — не повод выкидывать на /login:
// повторяем, пока сервис не поднимется. 401/403 — ответ по существу, его не повторяем.
const RETRY_DELAYS_MS = [1000, 2000, 3000, 5000];
const isTransient = (error) =>
  Boolean(error?.isAxiosError) && (!error.response || error.response.status >= 500);

const withTransientRetry = async (fn) => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (!isTransient(error) || attempt >= RETRY_DELAYS_MS.length) throw error;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
    }
  }
};

// Create authentication context
const AuthContext = createContext(null);

// Hook to access the authentication context
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const permissionsCacheRef = React.useRef(new Map());
  const navigate = useNavigate();
  const location = useLocation();

  // Тримаємо поточну сторінку в ref, щоб handleAuthLogout (підписка на подію,
  // не пере-створюється при кожній навігації) завжди бачив свіже значення —
  // потрібно, щоб після розлогіну через протухлий токен повернути користувача
  // саме туди, де це сталось (як зроблено через sessionStorage в my-profile-service).
  const locationRef = React.useRef(location);
  React.useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // Ref to store the token validation interval
  const tokenValidationInterval = React.useRef(null);

  // Load user permissions from user object
  const loadUserPermissions = useCallback((user) => {
    if (!user?.id) {
      setUserPermissions([]);
      permissionsCacheRef.current = new Map();
      return;
    }

    // Get permissions from user object (assuming user.permissions exists)
    const permissions = user.permissions || [];

    if (Array.isArray(permissions) && permissions.length > 0) {
      setUserPermissions(permissions);

      // Cache permissions for faster access
      // Include both exact matches and wildcard patterns
      const cache = new Map();

      permissions.forEach(permission => {
        // Cache exact permission
        const exactKey = `${permission.service}.${permission.resource}.${permission.action}`;
        cache.set(exactKey, permission);

        // Cache wildcard patterns for quick lookup
        if (permission.service === '*' && permission.resource === '*' && permission.action === '*') {
          cache.set('*.*.*', permission);
        } else if (permission.service !== '*' && permission.resource === '*' && permission.action === '*') {
          cache.set(`${permission.service}.*.*`, permission);
        } else if (permission.service !== '*' && permission.resource !== '*' && permission.action === '*') {
          cache.set(`${permission.service}.${permission.resource}.*`, permission);
        }
      });

      permissionsCacheRef.current = cache;
    } else {
      setUserPermissions([]);
      permissionsCacheRef.current = new Map();
    }
  }, []);


  // Authentication check
  const checkAuth = useCallback(async () => {
    setLoading(true);
    // /oauth-callback обмінює accessToken на refresh-cookie сам (OAuthCallback.jsx) —
    // паралельна спроба silent refresh тут створила б гонку за той самий токен.
    // /login навмисно НЕ виключаємо: якщо є валідний localStorage-токен або жива
    // refresh-cookie з іншого застосунку AnyEmp (SSO), саме тут це і має
    // виявитись, інакше PublicOnlyRoute ніколи не побачить isAuthenticated=true
    // і вже залогінений користувач так і лишиться на формі логіну.
    const pathname = window.location?.pathname || '';
    if (pathname === '/oauth-callback') {
      setLoading(false);
      return;
    }

    // Check if token exists
    let token = localStorage.getItem('token');
    if (!token) {
      // Единый вход между приложениями AnyEmp. Access-токен лежит в
      // localStorage, а тот привязан к origin — сессия, начатая в другом
      // приложении, отсюда не видна. Refresh-кука видна: auth-service ставит
      // её без Domain, шлюз Domain тоже вырезает, поэтому она host-only для
      // ШЛЮЗА и уезжает с любым запросом с withCredentials, с какого бы origin
      // страница ни пришла. Без этой попытки мы покажем форму входа человеку,
      // который уже вошёл.
      try {
        await withTransientRetry(() => authService.refreshToken());
        token = localStorage.getItem('token');
      } catch {
        token = null;
      }
    }
    if (!token) {
      setUser(null);
      loadUserPermissions(null);
      setLoading(false);
      return;
    }

    // If user is already loaded, skip profile reload
    if (user) {
      setLoading(false);
      return;
    }

    try {
      // Load user profile - this implicitly validates the token
      // If token is invalid, axios interceptor will automatically refresh it
      const profileResponse = await withTransientRetry(() => authService.getCurrentUser());
      if (profileResponse?.user) {
        setUser(profileResponse.user);
        loadUserPermissions(profileResponse.user);
      } else {
        setUser(null);
        loadUserPermissions(null);
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        console.error('Auth check failed: 401, clearing session', error);
        localStorage.removeItem('token');
        setUser(null);
        loadUserPermissions(null);
      } else {
        console.warn('Auth check failed (network/server error), keeping session', error?.message);
      }
    } finally {
      setLoading(false);
    }
  }, [loadUserPermissions, user]);

  // Run auth check on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Setup periodic token validation (every 5 minutes)
  useEffect(() => {
    const pathname = window.location?.pathname || '';

    // Don't start validation interval on login pages
    if (pathname === '/login' || pathname === '/oauth-callback') {
      return;
    }

    // Only setup interval if user is authenticated
    if (!user) {
      return;
    }

    // Validate token every 5 minutes (300000ms) by fetching user profile
    const VALIDATION_INTERVAL = 5 * 60 * 1000;

    tokenValidationInterval.current = setInterval(async () => {
      try {
        // Fetch user profile to validate token and refresh user data
        // If token is invalid, axios interceptor will automatically refresh it
        const profileResponse = await authService.getCurrentUser();
        if (profileResponse?.user) {
          setUser(profileResponse.user);
          loadUserPermissions(profileResponse.user);
        } else {
          console.warn('⚠️ Token validation failed, user will be logged out');
          setUser(null);
          loadUserPermissions(null);
          navigate('/login', { replace: true });
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401) {
          console.warn('⚠️ Token validation: 401, logging out');
          localStorage.removeItem('token');
          setUser(null);
          loadUserPermissions(null);
          navigate('/login', { replace: true });
        } else {
          console.warn('⚠️ Token validation failed (network/server error), keeping session', error?.message);
        }
      }
    }, VALIDATION_INTERVAL);

    // Cleanup interval on unmount
    return () => {
      if (tokenValidationInterval.current) {
        clearInterval(tokenValidationInterval.current);
        tokenValidationInterval.current = null;
      }
    };
  }, [user, loadUserPermissions, navigate]);

  // Listen for auth:logout events from API interceptors
  // This happens when refresh token expires and we need to force logout
  useEffect(() => {
    const handleAuthLogout = (event) => {
      console.warn('🚪 Auth logout event received:', event.detail);

      // Clear user state
      setUser(null);
      loadUserPermissions(null);

      // Navigate to login with state information
      const currentLocation = locationRef.current;
      const from = currentLocation?.pathname && currentLocation.pathname !== '/login'
        ? currentLocation
        : undefined;
      navigate('/login', {
        replace: true,
        state: {
          reason: event.detail?.reason,
          message: 'Your session has expired. Please log in again.',
          from
        }
      });
    };

    // Add event listener
    window.addEventListener('auth:logout', handleAuthLogout);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, [navigate, loadUserPermissions]);

  // Login handler
  const login = async (email, password) => {
    setError(null);
    try {
      const response = await authService.login(email, password);
      
      if (response?.tokens?.accessToken || response?.token) {
        // After successful login, fetch fresh user data from /me
        try {
          const profileResponse = await authService.getCurrentUser();
          
          if (profileResponse?.user) {
            setUser(profileResponse.user);
            loadUserPermissions(profileResponse.user);
          } else {
            setUser(null);
            loadUserPermissions(null);
          }
        } catch (profileError) {
          setUser(null);
          loadUserPermissions(null);
        }
        // Навігацію після логіну робить виключно виклик — Login.jsx йде на
        // location.state.from (щоб повернути туди, звідки закинуло
        // ProtectedRoute), а не сюди на жорстко зашите '/'. Два navigate()
        // підряд (цей і той в onSubmit) — конкуруючі виклики: другий
        // спрацьовував раніше, ніж встигав відрендеритись перший, тому
        // повернення на from було нестабільним.
        return response;
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      // Store the raw error so the component can render the server message
      setError(error);
      throw error;
    }
  };

  // Google OAuth login handler
  const loginWithGoogle = () => {
    setError(null);
    try {
      authService.loginWithGoogle();
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  // Logout handler
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      loadUserPermissions(null);
      navigate('/login');
    } catch (error) {
      // Even if there is an error — still clear local state
      setUser(null);
      loadUserPermissions(null);
      navigate('/login');
    }
  };

  // Update profile handler
  const updateProfile = async (profileData) => {
    try {
      const response = await authService.updateProfile(profileData);
      if (response?.user) {
        setUser(response.user);
        loadUserPermissions(response.user);
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Change password handler
  const changePassword = async (passwordData) => {
    try {
      return await authService.changePassword(passwordData);
    } catch (error) {
      throw error;
    }
  };

  // Permission checking methods
  const hasPermission = useCallback((service, resource, action) => {
    const permissionKey = `${service}.${resource}.${action}`;
    // console.log(`🔐 [hasPermission] Checking permission: ${permissionKey}`, {
    //   service,
    //   resource,
    //   action,
    //   userPermissionsCount: userPermissions.length
    // });

    if (!userPermissions.length) {
      console.log(`❌ [hasPermission] No permissions found for user`);
      return false;
    }
    
    // Check cache first for exact match
    const key = `${service}.${resource}.${action}`;
    if (permissionsCacheRef.current.has(key)) {
      // console.log(`✅ [hasPermission] Permission found in cache: ${key}`);
      return true;
    }
    
    // Check for wildcard permissions
    // 1. Check for global wildcard: *.*.*
    const globalWildcard = userPermissions.some(permission => 
      permission.service === '*' && 
      permission.resource === '*' && 
      permission.action === '*'
    );
    if (globalWildcard) {
      // console.log(`✅ [hasPermission] Global wildcard permission found: *.*.*`);
      permissionsCacheRef.current.set(key, true);
      return true;
    }
    
    // 2. Check for service wildcard: service.*.*
    const serviceWildcard = userPermissions.some(permission => 
      permission.service === service && 
      permission.resource === '*' && 
      permission.action === '*'
    );
    if (serviceWildcard) {
      // console.log(`✅ [hasPermission] Service wildcard permission found: ${service}.*.*`);
      permissionsCacheRef.current.set(key, true);
      return true;
    }
    
    // 3. Check for resource wildcard: service.resource.*
    const resourceWildcard = userPermissions.some(permission => 
      permission.service === service && 
      permission.resource === resource && 
      permission.action === '*'
    );
    if (resourceWildcard) {
      // console.log(`✅ [hasPermission] Resource wildcard permission found: ${service}.${resource}.*`);
      permissionsCacheRef.current.set(key, true);
      return true;
    }
    
    // 4. Check for action wildcard: service.*.action
    const actionWildcard = userPermissions.some(permission => 
      permission.service === service && 
      permission.resource === '*' && 
      permission.action === action
    );
    if (actionWildcard) {
      // console.log(`✅ [hasPermission] Action wildcard permission found: ${service}.*.${action}`);
      permissionsCacheRef.current.set(key, true);
      return true;
    }
    
    // 5. Check for exact match
    const hasExactPerm = userPermissions.some(permission => 
      permission.service === service && 
      permission.resource === resource && 
      permission.action === action
    );
    
    if (hasExactPerm) {
      // console.log(`✅ [hasPermission] Exact permission match found: ${permissionKey}`);
      permissionsCacheRef.current.set(key, true);
    } else {
      // console.log(`❌ [hasPermission] Permission denied: ${permissionKey}`, {
      //   availablePermissions: userPermissions.map(p => `${p.service}.${p.resource}.${p.action}`)
      // });
    }
    
    return hasExactPerm;
  }, [userPermissions]);


  // Role check
  const hasRole = (role) => {
    if (!user) return false;
    return user.role === role;
  };

  // Any-of-roles check
  const hasAnyRole = (roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  // Additional permission methods
  const hasServiceAccess = useCallback((service) => {
    if (!userPermissions.length) return false;
    
    // Check for global wildcard
    const hasGlobalWildcard = userPermissions.some(permission => 
      permission.service === '*' && permission.resource === '*' && permission.action === '*'
    );
    if (hasGlobalWildcard) return true;
    
    // Check for service wildcard
    const hasServiceWildcard = userPermissions.some(permission => 
      permission.service === service && permission.resource === '*' && permission.action === '*'
    );
    if (hasServiceWildcard) return true;
    
    // Check for any permission in the service
    return userPermissions.length > 0 && userPermissions.some(permission => permission.service === service);
  }, [userPermissions]);

  const hasResourceAccess = useCallback((service, resource) => {
    if (!userPermissions.length) return false;
    
    // Check for global wildcard
    const hasGlobalWildcard = userPermissions.some(permission => 
      permission.service === '*' && permission.resource === '*' && permission.action === '*'
    );
    if (hasGlobalWildcard) return true;
    
    // Check for service wildcard
    const hasServiceWildcard = userPermissions.some(permission => 
      permission.service === service && permission.resource === '*' && permission.action === '*'
    );
    if (hasServiceWildcard) return true;
    
    // Check for resource wildcard
    const hasResourceWildcard = userPermissions.some(permission => 
      permission.service === service && permission.resource === resource && permission.action === '*'
    );
    if (hasResourceWildcard) return true;
    
    // Check for any permission in the resource
    return userPermissions.some(permission => 
      permission.service === service && permission.resource === resource
    );
  }, [userPermissions]);

  const hasAllPermissions = useCallback((permissions) => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.every(([service, resource, action]) => 
      hasPermission(service, resource, action)
    );
  }, [hasPermission]);

  const hasAnyPermission = useCallback((permissions) => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.some(([service, resource, action]) => 
      hasPermission(service, resource, action)
    );
  }, [hasPermission]);

  // Simplified permission helpers - use hasPermission directly in components
  // Dashboard доступен всем аутентифицированным пользователям
  const canViewDashboard = useCallback(() => true, []);

  // Get token function
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Context value
  const value = useMemo(() => ({
    // User state
    user,
    loading,
    error,
    isAuthenticated: !!user,

    // Authentication methods
    login,
    loginWithGoogle,
    logout,
    updateProfile,
    changePassword,
    checkAuth,
    setUser,
    loadUserPermissions,
    getToken,

    // Role methods
    hasRole,
    hasAnyRole,

    // Permission methods (simplified)
    hasPermission,
    hasServiceAccess,
    hasResourceAccess,
    hasAllPermissions,
    hasAnyPermission,

    // Dashboard access (special case)
    canViewDashboard,

    // Permission state
    userPermissions,
  }), [
    user,
    loading,
    error,
    login,
    loginWithGoogle,
    logout,
    updateProfile,
    changePassword,
    checkAuth,
    setUser,
    loadUserPermissions,
    getToken,
    hasRole,
    hasAnyRole,
    hasPermission,
    hasServiceAccess,
    hasResourceAccess,
    hasAllPermissions,
    hasAnyPermission,
    canViewDashboard,
    userPermissions,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;